import {
    RiArrowLeftLine, RiCalendarEventLine,
    RiGroup2Line,
    RiRestartLine,
} from "@remixicon/react";
import React, {
    useEffect,
    useRef,
    useState,
} from "react";

import { Article, ArticleTag } from "../../__generated__/api";
import NoResults from "../../components/agent-v3/board/no-results/NoResults";
import Badge from "../../components/theme/badge/Badge";
import Button from "../../components/theme/button/Button";
import Time from "../../components/time/Time";
import Styles from "./UpdatesContext.module.scss";
import { LoggingHandler } from "../logging/LoggingContext";
import ArticleView from "../../pages/answers/details-v3/Article";

export default function UpdateDisplay({
    articles,
    selectedCategory,
    lastRead,
}: {
    articles: Article[];
    selectedCategory: ArticleTag | undefined;
    lastRead: number;
}) {
    const [selectedArticle, setSelectedArticle] = useState<Article>();
    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setSelectedArticle(undefined);
    }, [selectedCategory]);

    let displayList = articles;
    if (selectedCategory) {
        displayList = displayList.filter(row => row.tags.some(tag => tag.id === selectedCategory.id));
    } else {
        displayList = displayList.sortByDescending(row => new Date(row.publishedDate).getTime()).limit(10);
    }
    if (displayList.isEmpty) {
        return (
            <NoResults
                title="No Recent Updates"
                className={Styles["no-results"]}
                text="Try resetting your filter to look for more updates."
                resetIcon={<RiRestartLine />}
            />
        );
    }
    if (selectedArticle) {
        return (
            <div className={Styles["display-article"]} ref={scrollRef}>
                <Button
                    onClick={() => setSelectedArticle(undefined)}
                    prefixIcon={<RiArrowLeftLine />}
                    className={Styles["display-button-back"]}
                    metricPath={[LoggingHandler.Metrics.UPDATES, "back"]}
                    variant="none"
                >
                    Back
                </Button>
                <h2 className={Styles["display-title"]}>
                    {selectedArticle.title}
                </h2>
                <div className={Styles["display-line"]}>
                    <div className={Styles["display-date"]}>
                        <RiCalendarEventLine />
                        {" "}
                        <Time date={selectedArticle.publishedDate} variant="date" />
                    </div>
                    {selectedArticle.publishedBy && (
                        <div className={Styles["display-publisher"]}>
                            <RiGroup2Line />
                            {" "}
                            {selectedArticle.publishedBy.name}
                        </div>
                    )}

                    <div className={Styles["display-tags"]}>
                        {selectedArticle.tags.map(tag => (
                            <Badge key={tag.id} variant="gray" className={Styles["display-tag"]}>{tag.title}</Badge>
                        ))}
                    </div>
                </div>

                <div className={Styles["display-article-body"]}>
                    <ArticleView
                        article={selectedArticle}
                        isReadOnly
                        shouldShowHeader={false}
                        scrollRef={scrollRef}
                    />
                </div>

            </div>
        );
    }
    return (
        <div className={Styles["updates-list"]}>
            {displayList.map(update => (
                <Button
                    key={update.id}
                    onClick={() => setSelectedArticle(update)}
                    className={Styles["update-list-item"]}
                    variant="none"
                    metricPath={[LoggingHandler.Metrics.UPDATES, "open", update.id]}
                >
                    <div className={Styles["update-date"]}>
                        {lastRead < new Date(update.publishedDate).getTime() && (
                            <Badge className={Styles["update-new"]} size="small" variant="main">New</Badge>
                        )}
                        <RiCalendarEventLine />
                        {" "}
                        <Time date={update.publishedDate} variant="date" />

                    </div>
                    <h4 className={Styles["update-title"]}>
                        {update.title}
                    </h4>
                    <p className={Styles["update-preview"]}>
                        {update.previewText}
                    </p>
                    <div className={Styles["update-tags"]}>
                        {update.tags.map(tag => (
                            <Badge key={tag.id} variant="gray" className={Styles["update-tag"]}>{tag.title}</Badge>
                        ))}
                    </div>
                </Button>
            ))}
        </div>
    );
}
